import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../core/auth/auth.service';

type VerifyState = 'verifying' | 'token_expired' | 'already_used' | 'invalid_token' | 'error';

@Component({
  selector: 'app-verify-email',
  imports: [
    RouterLink,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './verify-email.html',
  styleUrl: './verify-email.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerifyEmailComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected verifyState = signal<VerifyState>('verifying');
  protected resendEmail = signal<string>('');
  protected resendSent = signal(false);
  protected resendSending = signal(false);
  protected countdown = signal<number | null>(null);

  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.clearCountdown());
    // If already authenticated (e.g. token used on a previous load), go directly to next step.
    if (this.authService.isAuthenticated()) {
      this.navigatePostVerification();
      return;
    }

    const token = this.route.snapshot.queryParamMap.get('token');
    if (!token) {
      this.verifyState.set('invalid_token');
      return;
    }

    this.authService.verifyEmail(token).subscribe({
      next: () => {
        this.navigatePostVerification();
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          const authErr = this.authService.getAuthError(err);
          switch (authErr?.code) {
            case 'TOKEN_EXPIRED':
              this.verifyState.set('token_expired');
              if (authErr.email) this.resendEmail.set(authErr.email);
              break;
            case 'TOKEN_ALREADY_USED':
              this.verifyState.set('already_used');
              break;
            default:
              this.verifyState.set('invalid_token');
          }
        } else {
          this.verifyState.set('error');
        }
      },
    });
  }

  private navigatePostVerification(): void {
    if (this.authService.profileCompleted()) {
      void this.router.navigate(['/dashboard']);
    } else {
      void this.router.navigate(['/auth/complete-profile']);
    }
  }

  protected onResend(): void {
    const email = this.resendEmail();
    if (!email || this.resendSending() || this.countdown() !== null) return;
    this.resendSending.set(true);
    this.authService.resendVerification(email).subscribe({
      next: () => {
        this.resendSending.set(false);
        this.resendSent.set(true);
      },
      error: (err: unknown) => {
        this.resendSending.set(false);
        if (err instanceof HttpErrorResponse) {
          const authErr = this.authService.getAuthError(err);
          if (authErr?.code === 'RESEND_RATE_LIMITED' && authErr.retry_after) {
            this.startCountdown(authErr.retry_after);
            return;
          }
        }
        this.resendSent.set(true);
      },
    });
  }

  private startCountdown(seconds: number): void {
    this.clearCountdown();
    this.countdown.set(seconds);
    this.countdownTimer = setInterval(() => {
      const remaining = (this.countdown() ?? 1) - 1;
      if (remaining <= 0) {
        this.clearCountdown();
      } else {
        this.countdown.set(remaining);
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.countdown.set(null);
  }
}
